import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd'
import * as XLSX from 'xlsx'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import {
  SALE_ORDER_STATUS_DROPDOWN_OPTIONS,
  SALE_ORDER_STATUS_NAME,
} from 'src/models/sale-orders'
import WidgetCustomerSelector from 'src/widgets/Selectors/WidgetCustomerSelector'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import { YesNoOptions } from 'src/helpers/commons/dropdownOptions'
import { wtRoundValue } from 'src/helpers/commons/utils'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'

const SaleOrderDeliveryDetailReport = () => {
  const Base = new BaseListing('sale_order_report')
  const { formatDate, formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [branchId, setBranchId] = useState(0)
  const [DepartmentId, setDepartmentId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [DepartmentName, setDepartmentName] = useState('')
  const [SoDData, setSaleOrderDeliveryDetailReportData] = useState([])
  const branches = wtGetFromLocalStorage('branches')
  const [customers, setCustomers] = useState([])
  const [departments, setDepartments] = useState([])
  const [items, setItems] = useState([])
  const [itemId, setItemId] = useState(0)
  const [itemName, setItemName] = useState('')
  const [searchMore, setSearchMore] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(null)
  const [Status, setStatus] = useState(null)
  const [showItemSerials, setShowItemSerials] = useState(2)

  const changeValues = (key, val) => {
    setSaleOrderDeliveryDetailReportData([])
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'department_id') {
      setDepartmentId(val.value)
      setDepartmentName(val.label)
    }
    if (key === 'item_id') {
      setItemId(val?.value || 0)
      setItemName(val?.label || '')
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'type') {
      setVoucherType(val.value)
      setVoucherTypeName(val.label)
    }
    if (key === 'customer_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'status') {
      setStatus(val.value)
    }
    if (key === 'show_item_serials') {
      setShowItemSerials(val?.value ?? 2)
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'customers,departments,items',
      {},
      [setCustomers, setDepartments, setItems],
      1,
    )
  }, [])

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]
    setIsLoading(true)
    wtApiCall(
      'reports/sale_order_delivery_detail_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        party_id: PartyId,
        department_id: DepartmentId,
        status: Status,
        item_id: itemId,
        show_item_serials: showItemSerials,
      },
      'get',
      (response) => {
        console.log(response.data)
        setSaleOrderDeliveryDetailReportData(response.data)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToExcel = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/sale_order_delivery_detail_report',
      {
        branch_id: branchId,
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        party_id: PartyId,
        department_id: DepartmentId,
        status: Status,
        item_id: itemId,
        show_item_serials: 1,
      },
      'get',
      (response) => {
        const reportData = response?.data || []
        const detailRows = reportData.map((record, index) => ({
          SR: index + 1,
          'DO NO#': record.sale_order_delivery?.code || '',
          'SO NO#': record.sale_order?.code || '',
          Branch: record.branch?.name || '',
          Department: record.sale_order?.department?.name || '',
          'Customer Name': record.sale_order?.party?.name || '',
          'Sub Customer Name':
            record.sale_order?.party?.retail_customer === 1
              ? record.sale_order?.retail_customer_name || ''
              : '',
          'NTN/CNIC Number':
            record.sale_order?.party?.retail_customer === 1
              ? record.sale_order?.retail_customer_cnic || ''
              : record.sale_order?.party?.ntn || record.sale_order?.party?.cnic || '',
          'Revenue Account': record.sale_order?.revenue_account?.name || '',
          'DO Date': formatDate(record.sale_order_delivery?.delivery_date),
          'Item Name': record.item_name || '',
          'Item Unit': record.item_unit?.name || '',
          'Item Brand': record.item_brand?.name || '',
          Warehouse: record.sale_order_delivery?.warehouse?.name || '',
          'Creation Date': formatDateTime(record.sale_order_delivery?.created_at),
          'Creation By': record.sale_order_delivery?.created_by?.name || '',
          'Approved By': record.sale_order_delivery?.hod_approved_by?.name || '',
          'Approved At': formatDateTime(record.sale_order_delivery?.hod_approved_at),
          'DO Verified By': record.sale_order_delivery?.approved_by?.name || '',
          'DO Verified At': formatDateTime(record.sale_order_delivery?.approved_at),
          'Delivering QTY': wtRoundValue(record.delivering_quantity || 0),
          ...(showItemSerials === 1
            ? { 'Item Serials': record.item_serial_numbers || '' }
            : {}),
          'Unit Price': wtRoundValue(record.sale_order_detail?.unit_price || 0),
          'Discount Amount': wtRoundValue(record.delivered_discount || 0),
          'Tax Amount': wtRoundValue(record.delivered_tax || 0),
          'Further Tax Name': record.further_tax_name || '',
          'Further Tax Rate': wtRoundValue(record.additional_tax_rate || 0, 2) + '%',
          'Further Tax': wtRoundValue(record.further_tax || 0),
          'Grand Total': wtRoundValue(record.grand_total || 0),
          'Income Tax': wtRoundValue(record.income_tax_amount || 0),
          'Net Total': wtRoundValue(
            (record.grand_total || 0) + (record.income_tax_amount || 0),
          ),
          'Unit Cost': wtRoundValue(record.sale_order_detail?.unit_cost || 0, 3),
          'Total Cost': wtRoundValue(
            (record.sale_order_detail?.unit_cost || 0) * (record.delivering_quantity || 0),
            3,
          ),
        }))

        const serialRows = reportData.flatMap((record) =>
          (record.item_serials || [])
            .filter((itemSerial) => itemSerial?.serial)
            .map((itemSerial, serialIndex) => ({
              SR: serialIndex + 1,
              'DO NO#': record.sale_order_delivery?.code || '',
              'SO NO#': record.sale_order?.code || '',
              'Item Name': record.item_name || '',
              'Item Brand': record.item_brand?.name || '',
              'Item Unit': record.item_unit?.name || '',
              Serial: itemSerial.serial,
            })),
        )

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(detailRows),
          'Delivery Details',
        )
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(serialRows),
          'Item Serials',
        )
        XLSX.writeFile(workbook, 'sale_order_delivery_detail_report.xlsx')
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }
  const columns = [
    {
      key: 'sr',
      title: 'SR',
      render: (text, record, index) => index + 1, // Adds Serial Number
      fixed: 'left',
      width: 50,
    },
    {
      key: 'code',
      title: 'DO NO#',
      dataIndex: 'code',
      fixed: 'left',
      width: 90,
      render: (text, record) => record.sale_order_delivery?.code || '-',
    },
    {
      key: 'sale_order_id',
      title: 'SO NO#',
      dataIndex: 'sale_order_id',
      width: 90,
      render: (text, record) => record.sale_order?.code || '-',
    },
    {
      key: 'branch_id',
      title: 'Branch',
      render: (text, record) => (record.branch ? record.branch.name : ''),
      width: 80,
    },
    {
      key: 'department_id',
      title: 'Department',
      width: 150,
      render: (_, record) => record.sale_order?.department?.name || '-',
    },
    {
      key: 'party_id',
      title: 'Customer Name',
      render: (_, record) => record.sale_order?.party?.name || '-',
    },
    {
      key: 'sub_customer_name',
      title: 'Sub Customer Name',
      render: (_, record) => {
        const isRetailCustomer = record?.sale_order?.party?.retail_customer === 1
        if (!isRetailCustomer) return '-'
        return record?.sale_order?.retail_customer_name || '-'
      },
    },
    {
      key: 'ntn_cnic_number',
      title: 'NTN/CNIC Number',
      render: (_, record) => {
        const isRetailCustomer = record?.sale_order?.party?.retail_customer === 1
        if (isRetailCustomer) {
          return record?.sale_order?.retail_customer_cnic || '-'
        }
        return record?.sale_order?.party?.ntn || record?.sale_order?.party?.cnic || '-'
      },
    },
    {
      key: 'revenue_account_id',
      title: 'Revenue Account',
      render: (_, record) => record?.sale_order?.revenue_account?.name ?? '-',
    },
    {
      key: 'delivery_date',
      title: 'DO Date',
      render: (text, record) => formatDate(record.sale_order_delivery?.delivery_date),
      width: 150,
    },
    {
      key: 'item_name',
      title: 'Item Name',
      dataIndex: 'item_name',
    },
    {
      key: 'item_unit_id',
      title: 'Item Unit',
      render: (text, record) => (record.item_unit ? record.item_unit.name : ''),
    },
    {
      key: 'item_brand_id',
      title: 'Item Brand',
      render: (text, record) => record.item_brand?.name || '-',
    },
    {
      key: 'warehouse',
      title: 'Warehouse',
      render: (text, record) => record.sale_order_delivery?.warehouse?.name || '-',
      // width: 150,
    },
    {
      key: 'created_at',
      title: 'Creation Date',
      width: 170,
      render: (_, record) => formatDateTime(record.sale_order_delivery?.created_at),
    },
    {
      key: 'created_by',
      title: 'Creation By',
      width: 150,
      render: (_, record) => record.sale_order_delivery?.created_by?.name || '-',
    },
    {
      key: 'hod_approved_by',
      title: 'Approved By',
      width: 150,
      render: (_, record) => record.sale_order_delivery?.hod_approved_by?.name || '-',
    },
    {
      key: 'hod_approved_at',
      title: 'Approved At',
      width: 170,
      render: (_, record) => formatDateTime(record.sale_order_delivery?.hod_approved_at),
    },
    {
      key: 'approved_by',
      title: 'DO Verified By',
      width: 150,
      render: (_, record) => record.sale_order_delivery?.approved_by?.name || '-',
    },
    {
      key: 'approved_at',
      title: 'DO Verified At',
      width: 170,
      render: (_, record) => formatDateTime(record.sale_order_delivery?.approved_at),
    },
    {
      key: 'delivering_quantity',
      title: 'Delivering QTY',
      render: (text, record) => wtRoundValue(record.delivering_quantity),
    },
    ...(showItemSerials === 1
      ? [
          {
            key: 'item_serials',
            title: 'Item Serials',
            width: 260,
            render: (_, record) => {
              const serials =
                record?.item_serial_numbers ||
                record?.item_serials?.map((item) => item.serial).filter(Boolean).join(', ')
              return serials || '-'
            },
          },
        ]
      : []),
    {
      key: 'unit_price',
      title: 'Unit Price',
      render: (text, record) => wtRoundValue(record.sale_order_detail?.unit_price || 0),
    },
    {
      key: 'discount_amount',
      title: 'Discount Amount',
      render: (text, record) => {
        const { quantity, discount_amount } = record.sale_order_detail || {}
        const delivering_qty = record.delivering_quantity || 0
        if (!quantity || !discount_amount) return 0
        return wtRoundValue((delivering_qty / quantity) * discount_amount)
      },
    },
    {
      key: 'tax_amount',
      title: 'Tax Amount',
      render: (text, record) => {
        const { quantity, tax_amount } = record.sale_order_detail || {}
        const delivering_qty = record.delivering_quantity || 0
        if (!quantity || !tax_amount) return 0
        return wtRoundValue((delivering_qty / quantity) * tax_amount)
      },
    },
    {
      key: 'further_tax_name',
      title: 'Further Tax Name',
      render: (_, record) => record.further_tax_name || '-',
      width: 150,
    },
    {
      key: 'additional_tax_rate',
      title: 'Further Tax Rate',
      render: (_, record) => `${wtRoundValue(record.additional_tax_rate || 0, 2)}%`,
      width: 120,
    },
    {
      key: 'further_tax',
      title: 'Further Tax',
      render: (_, record) => wtRoundValue(record.further_tax || 0),
      width: 120,
    },
    {
      key: 'grand_total',
      title: 'Grand Total',
      render: (_, record) => wtRoundValue(record.grand_total || 0),
      width: 120,
    },
    {
      key: 'income_tax_amount',
      title: 'Income Tax',
      render: (_, record) => wtRoundValue(record.income_tax_amount || 0),
      width: 120,
    },
    {
      key: 'net_total',
      title: 'Net Total',
      render: (_, record) => wtRoundValue((record.grand_total || 0) + (record.income_tax_amount || 0)),
    },
    {
      key: 'unit_cost',
      title: 'Unit Cost',
      render: (text, record) => wtRoundValue(record.sale_order_detail?.unit_cost || 0, 3),
    },
    {
      key: 'total_cost',
      title: 'Total Cost',
      render: (text, record) =>
        wtRoundValue(
          (record.sale_order_detail?.unit_cost || 0) * record.delivering_quantity,
          3,
        ),
    },
  ]
  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          title={
            <span style={{ fontSize: '26px', marginLeft: '45%' }}>
              Sale Order Delivery Detail Report
            </span>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={5}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value)
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            {/* <Col xs={15} sm={10} md={5}>
              <label>Department:</label>
              <WidgetDepartmentSelector
                value={DepartmentId}
                displayValue={DepartmentName}
                departments={departments}
                placeholder="Select Department"
                onChange={(values) => changeValues('department_id', values)}
                showCloudIcon={false}
              />
            </Col> */}
            <Col xs={15} sm={10} md={5}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Customer</label>
              <WidgetCustomerSelector
                onChange={(value) => changeValues('customer_id', value)}
                customers={customers}
                displayValue={PartyName}
                value={PartyId}
                reloadDropdownData={() => reloadParties()}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Item:</label>
              <WidgetItemSelector
                onChange={(value) => changeValues('item_id', value)}
                items={items}
                value={itemId}
                displayValue={itemName}
                placeHolder="All Items"
                reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Status:</label>
              <WidgetSelector
                options={SALE_ORDER_STATUS_DROPDOWN_OPTIONS}
                selectedValue={Status}
                displayValue={SALE_ORDER_STATUS_NAME[Status]}
                onChange={(value) => changeValues('status', value)}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Show Item Serials:</label>
              <WidgetSelector
                options={YesNoOptions}
                selectedValue={showItemSerials}
                displayValue={YesNoOptions.find((item) => item.value === showItemSerials)?.label}
                onChange={(value) => changeValues('show_item_serials', value)}
              />
            </Col>
            <Col xs={24} sm={24} md={6}>
              <div
                style={{
                  marginTop: 30,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'nowrap',
                }}
              >
                <Button type="primary" htmlType="submit" disabled={isLoading ? true : false}>
                  Search
                </Button>
                <Button
                  style={{ backgroundColor: '#4BB543', color: '#fff' }}
                  onClick={exportToExcel}
                  disabled={isLoading ? true : false}
                >
                  Export
                </Button>
                <Button
                  style={{ color: '#fff', backgroundColor: '#E29C06' }}
                  onClick={() => setSearchMore(!searchMore)}
                  disabled={isLoading ? true : false}
                >
                  {searchMore ? 'Less...' : 'More...'}
                </Button>
              </div>
            </Col>
          </Row>
        </Card>
      </Form>
      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={SoDData}
            rowKey="id"
            pagination={{
              defaultPageSize: 100,
              pageSizeOptions: ['50', '100', '250', '500'],
              showSizeChanger: true,
            }}
            bordered
            scroll={{ x: 3200, y: 400 }}
          />
        </Spin>
      </Card>
    </div>
  )
}
export default SaleOrderDeliveryDetailReport
